function checkArray(arr) {
	return Array.isArray(arr) ? arr : [];
}

function isNil(value) {
	return value === null || value === undefined;
}

function normalizeId(value) {
	if (isNil(value) || value === '') return null;
	const normalized = Number(value);
	return Number.isNaN(normalized) ? value : normalized;
}

function getScopeValue(source, snakeCaseKey, camelCaseKey) {
	return normalizeId(
		!isNil(source?.[snakeCaseKey])
			? source[snakeCaseKey]
			: source?.[camelCaseKey],
	);
}

export function normalizeAssignmentScope(assignment = {}) {
	return {
		departmentId: getScopeValue(assignment, 'department_id', 'departmentId'),
		orderTargetId: getScopeValue(
			assignment,
			'order_target_id',
			'orderTargetId',
		),
		isDefault: Boolean(
			!isNil(assignment.is_default)
				? assignment.is_default
				: assignment.isDefault,
		),
	};
}

function getTemplateId(assignment = {}) {
	return normalizeId(
		!isNil(assignment.id)
			? assignment.id
			: !isNil(assignment.template_id)
				? assignment.template_id
				: assignment.templateId,
	);
}

function getOrderTargets(department = {}) {
	return checkArray(department.order_targets || department.orderTargets);
}

export function findOrderTargetDepartment(departments, orderTargetId) {
	const normalizedOrderTargetId = normalizeId(orderTargetId);
	if (normalizedOrderTargetId === null) return null;

	for (const department of checkArray(departments)) {
		const orderTarget = getOrderTargets(department).find(
			item => normalizeId(item?.id) === normalizedOrderTargetId,
		);
		if (orderTarget) return { department, orderTarget };
	}

	return null;
}

export function getTemplateAssignmentKey(assignment = {}) {
	if (isNil(assignment)) return null;

	const templateId = getTemplateId(assignment);
	if (templateId === null) return null;

	const { departmentId, orderTargetId } = normalizeAssignmentScope(assignment);
	return [
		`template:${templateId}`,
		`department:${departmentId === null ? 'none' : departmentId}`,
		`orderTarget:${orderTargetId === null ? 'none' : orderTargetId}`,
	].join('|');
}

export function findTemplateAssignment({
	assignments,
	assignmentKey,
	templateId,
}) {
	const candidates = checkArray(assignments);
	if (assignmentKey) {
		const assignment = candidates.find(
			item => getTemplateAssignmentKey(item) === assignmentKey,
		);
		return assignment || null;
	}

	const normalizedTemplateId = normalizeId(templateId);
	if (normalizedTemplateId === null) return null;

	return (
		candidates.find(item => getTemplateId(item) === normalizedTemplateId) ||
		null
	);
}

export function getTemplateAssignmentScopeLabel(assignment, departments) {
	const { departmentId, orderTargetId } = normalizeAssignmentScope(assignment);
	if (departmentId === null && orderTargetId === null) return 'Global';

	const catalog = checkArray(departments);
	const department = catalog.find(
		item => normalizeId(item?.id) === departmentId,
	);
	const targetOwner = findOrderTargetDepartment(catalog, orderTargetId);
	const departmentName =
		department?.name ||
		targetOwner?.department?.name ||
		(departmentId === null ? null : `Departamento ${departmentId}`);

	if (orderTargetId !== null) {
		const orderTargetName =
			targetOwner?.orderTarget?.name || `Order target ${orderTargetId}`;
		return `Línea de negocio: ${
			departmentName ? `${departmentName} / ` : ''
		}${orderTargetName}`;
	}

	return `Departamento: ${departmentName}`;
}

export function getDefinitionTemplatesByType({ templates, type }) {
	return checkArray(templates).filter(t => t.template_type_id === type);
}

export function filterAssignedTemplatesByProjects({
	assignments,
	profile,
	departments,
}) {
	const projectIds = checkArray(profile?.user?.assignment?.projects).map(
		normalizeId,
	);

	return checkArray(assignments).filter(assignment => {
		const { departmentId, orderTargetId } =
			normalizeAssignmentScope(assignment);
		if (departmentId === null && orderTargetId === null) return true;
		if (departmentId !== null) return projectIds.includes(departmentId);

		const targetOwner = findOrderTargetDepartment(departments, orderTargetId);
		return (
			targetOwner !== null &&
			projectIds.includes(normalizeId(targetOwner.department.id))
		);
	});
}

export function resolveAssignedTemplate(assignments, context = {}) {
	const { departmentId, orderTargetId } = normalizeAssignmentScope(context);
	const candidates = checkArray(assignments);
	const preferNonDefault = scopedAssignments =>
		scopedAssignments.find(
			assignment => !normalizeAssignmentScope(assignment).isDefault,
		) || scopedAssignments[0];

	if (orderTargetId !== null) {
		const orderTargetAssignments = candidates.filter(
			assignment =>
				normalizeAssignmentScope(assignment).orderTargetId === orderTargetId,
		);
		if (orderTargetAssignments.length) {
			return preferNonDefault(orderTargetAssignments);
		}
	}

	if (departmentId !== null) {
		const departmentAssignments = candidates.filter(assignment => {
			const assignmentScope = normalizeAssignmentScope(assignment);
			return (
				assignmentScope.departmentId === departmentId &&
				assignmentScope.orderTargetId === null
			);
		});
		if (departmentAssignments.length) {
			return preferNonDefault(departmentAssignments);
		}
	}

	const globalAssignments = candidates.filter(assignment => {
		const assignmentScope = normalizeAssignmentScope(assignment);
		return (
			assignmentScope.departmentId === null &&
			assignmentScope.orderTargetId === null
		);
	});

	return preferNonDefault(globalAssignments) || null;
}

class ApiUtils {
	static getDefinitionTemplatesByType({ templates, type }) {
		return getDefinitionTemplatesByType({ templates, type });
	}

	static normalizeAssignmentScope(assignment) {
		return normalizeAssignmentScope(assignment);
	}

	static findOrderTargetDepartment(departments, orderTargetId) {
		return findOrderTargetDepartment(departments, orderTargetId);
	}

	static getTemplateAssignmentKey(assignment) {
		return getTemplateAssignmentKey(assignment);
	}

	static findTemplateAssignment(params) {
		return findTemplateAssignment(params);
	}

	static getTemplateAssignmentScopeLabel(assignment, departments) {
		return getTemplateAssignmentScopeLabel(assignment, departments);
	}

	static filterAssignedTemplatesByProjects(params) {
		return filterAssignedTemplatesByProjects(params);
	}

	static resolveAssignedTemplate(assignments, context = {}) {
		return resolveAssignedTemplate(assignments, context);
	}
}

export default ApiUtils;
